import { useState, useEffect, useCallback, useRef } from 'react';
import { Message } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface WorkerConnectionState {
  sessionId: string | null;
  connectionStatus: 'connected' | 'reconnecting' | 'offline';
  isConnected: boolean;
  error: string | null;
}

export interface WorkerConnectionActions {
  initializeSession: () => Promise<string | null>;
  sendMessage: (
    text: string,
    email?: string,
    name?: string,
    turnstileToken?: string
  ) => Promise<{ userMessage: Message; assistantMessage?: Message } | null>;
  verifyTurnstile: (token: string) => Promise<boolean>;
  validateMX: (email: string) => Promise<{ valid: boolean; error?: string }>;
  onSSEMessage?: (message: Message) => void;
}

export function useWorkerConnection(
  onSSEMessage?: (message: Message) => void,
  isChatOpen: boolean = false,
  storedSessionId?: string,
  onSessionCreated?: (sessionId: string) => void
): WorkerConnectionState & WorkerConnectionActions {
  const [sessionId, setSessionId] = useState<string | null>(storedSessionId || null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'reconnecting' | 'offline'>('offline');
  const [error, setError] = useState<string | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const isIntentionallyClosingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const onSSEMessageRef = useRef(onSSEMessage);
  const heartbeatIntervalRef = useRef<number | null>(null);

  // Keep callback ref up to date
  useEffect(() => {
    onSSEMessageRef.current = onSSEMessage;
  }, [onSSEMessage]);

  // Initialize session - reuse stored session if available
  const initializeSession = useCallback(async (): Promise<string | null> => {
    try {
      setError(null);

      // If we have a stored session, verify it's still valid
      if (storedSessionId) {
        try {
          const verifyResponse = await fetch(`${API_BASE_URL}/api/session/${storedSessionId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (verifyResponse.ok) {
            const verifyData = (await verifyResponse.json()) as { success: boolean };
            if (verifyData.success) {
              // Session is still valid, reuse it
              console.log('[Session] Reusing stored session:', storedSessionId);
              setSessionId(storedSessionId);
              setConnectionStatus('connected');
              return storedSessionId;
            }
          }
        } catch (verifyErr) {
          console.warn('[Session] Failed to verify stored session, creating new one:', verifyErr);
        }
      }

      // Create new session
      const response = await fetch(`${API_BASE_URL}/api/session/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize session: ${response.status}`);
      }

      const data = (await response.json()) as { success: boolean; data: { session_id: string } };
      if (data.success && data.data?.session_id) {
        const newSessionId = data.data.session_id;
        console.log('[Session] Created new session:', newSessionId);
        setSessionId(newSessionId);
        setConnectionStatus('connected');

        // Notify parent to store the session
        onSessionCreated?.(newSessionId);

        return newSessionId;
      }

      throw new Error('Invalid response from session init');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize session';
      setError(errorMessage);
      setConnectionStatus('offline');
      console.error('Session initialization error:', err);
      return null;
    }
  }, [storedSessionId, onSessionCreated]);

  // Send message
  const sendMessage = useCallback(
    async (
      text: string,
      email?: string,
      name?: string,
      turnstileToken?: string
    ): Promise<{ userMessage: Message; assistantMessage?: Message } | null> => {
      if (!sessionId) {
        setError('Session not initialized');
        return null;
      }

      try {
        setError(null);
        const response = await fetch(`${API_BASE_URL}/api/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionId,
            text,
            email,
            name,
            turnstile_token: turnstileToken,
          }),
        });

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as {
            requires_turnstile?: boolean;
            error?: string;
          };
          if (response.status === 403 && errorData.requires_turnstile) {
            throw new Error('TURNSTILE_REQUIRED');
          }
          throw new Error(errorData.error || `Failed to send message: ${response.status}`);
        }

        const data = (await response.json()) as {
          success: boolean;
          data: {
            message_id: string;
            routing_mode: 'slack' | 'ai';
            fallback_reason?: string;
            notification_message_id?: string;
            slack_thread_ts?: string;
            response_id?: string;
            response_text?: string;
            estimated_wait?: number;
          };
        };
        if (data.success) {
          const userMessage: Message = {
            id: data.data.message_id,
            text,
            sender: email || 'User',
            email,
            timestamp: new Date().toISOString(),
            isFromSlack: false,
            routing_mode: data.data.routing_mode,
            fallback_reason: data.data.fallback_reason,
            isFallback: !!data.data.fallback_reason,
          };

          let assistantMessage: Message | undefined;
          if (data.data.response_text && data.data.response_id) {
            assistantMessage = {
              id: data.data.response_id,
              text: data.data.response_text,
              sender: 'Studio Graphene',
              timestamp: new Date().toISOString(),
              isFromSlack: false,
              routing_mode: 'ai',
            };
          }

          // Update last message timestamp to prevent duplicate messages
          const latestMsg = assistantMessage || userMessage;
          console.log('[sendMessage] Message sent successfully:', latestMsg.id);

          return { userMessage, assistantMessage };
        }

        throw new Error('Invalid response from message endpoint');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        console.error('Send message error:', err);
        return null;
      }
    },
    [sessionId]
  );

  // Verify Turnstile token
  const verifyTurnstile = useCallback(
    async (token: string): Promise<boolean> => {
      if (!sessionId) {
        setError('Session not initialized');
        return false;
      }

      try {
        setError(null);
        const response = await fetch(`${API_BASE_URL}/api/turnstile/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            session_id: sessionId,
          }),
        });

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(errorData.error || `Turnstile verification failed: ${response.status}`);
        }

        const data = (await response.json()) as { success: boolean };
        return data.success === true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Turnstile verification failed';
        setError(errorMessage);
        console.error('Turnstile verification error:', err);
        return false;
      }
    },
    [sessionId]
  );

  // Validate MX records
  const validateMX = useCallback(async (email: string): Promise<{ valid: boolean; error?: string }> => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/mx-validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(errorData.error || `MX validation failed: ${response.status}`);
      }

      const data = (await response.json()) as {
        success: boolean;
        data: { valid: boolean; error?: string };
      };
      if (data.success) {
        return {
          valid: data.data.valid,
          error: data.data.error,
        };
      }

      return { valid: false, error: 'Invalid response from MX validation' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'MX validation failed';
      setError(errorMessage);
      console.error('MX validation error:', err);
      return { valid: false, error: errorMessage };
    }
  }, []);

  // Setup WebSocket connection for real-time updates
  // Only connect when session exists AND chat is open
  useEffect(() => {
    if (!sessionId || !isChatOpen) {
      // Clean up connection when chat is closed
      if (websocketRef.current) {
        isIntentionallyClosingRef.current = true;
        websocketRef.current.close();
        websocketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    const connectWebSocket = () => {
      // Clean up any existing connection
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      // Clear any pending reconnection
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      setConnectionStatus('reconnecting');

      // Connect to WebSocket endpoint
      const wsUrl = API_BASE_URL.replace('http://', 'ws://').replace('https://', 'wss://');
      const ws = new WebSocket(`${wsUrl}/api/ws?sessionId=${sessionId}`);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        setConnectionStatus('connected');
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset attempts on successful connection

        // Start heartbeat to keep connection alive
        heartbeatIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onerror = (event) => {
        console.error('[WebSocket] Error:', event);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Closed:', event.code, event.reason);

        // Clean up heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Don't reconnect if we're intentionally closing
        if (isIntentionallyClosingRef.current) {
          isIntentionallyClosingRef.current = false;
          return;
        }

        // Don't reconnect if we've exceeded max attempts
        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setConnectionStatus('offline');
          setError('WebSocket connection failed after multiple attempts. Please refresh the page.');
          return;
        }

        setConnectionStatus('offline');

        // Exponential backoff: 2s, 4s, 8s, 16s, 32s
        const delay = Math.min(2000 * Math.pow(2, reconnectAttemptsRef.current), 32000);
        reconnectAttemptsRef.current++;

        console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = window.setTimeout(() => {
          connectWebSocket();
        }, delay);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'connected') {
            console.log('[WebSocket] Connection confirmed:', data.sessionId);
          } else if (data.type === 'history') {
            // Initial message history loaded
            console.log('[WebSocket] Received history:', data.count, 'messages');
            // History is handled by ChatWindow loading from DB on mount
            // We don't need to process it here to avoid duplicates
          } else if (data.type === 'message' && data.data && onSSEMessageRef.current) {
            // New message arrived
            const message: Message = {
              id: data.data.id,
              text: data.data.text,
              sender: data.data.sender,
              email: data.data.email || undefined,
              timestamp: data.data.timestamp,
              isFromSlack: data.data.isFromSlack || data.data.sender === 'slack',
              routing_mode: data.data.routing_mode,
              fallback_reason: data.data.fallback_reason,
              isFallback: !!data.data.fallback_reason,
            };

            console.log('[WebSocket] New message:', message.id);
            onSSEMessageRef.current(message);
          } else if (data.type === 'error') {
            console.error('[WebSocket] Server error:', data.error);
            setError(data.error || 'WebSocket server error');
          } else if (data.type === 'pong') {
            // Heartbeat response
            console.debug('[WebSocket] Pong received');
          }
        } catch (err) {
          console.error('[WebSocket] Message parse error:', err);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (websocketRef.current) {
        isIntentionallyClosingRef.current = true;
        websocketRef.current.close();
        websocketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [sessionId, isChatOpen]); // Only connect when session exists AND chat is open

  return {
    sessionId,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    error,
    initializeSession,
    sendMessage,
    verifyTurnstile,
    validateMX,
  };
}
